import { Component, OnInit, input } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { Observable, of } from 'rxjs';
import { ZiroSelectOption } from '../../../../../forms/fields/select/ziro-select.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import {
  ClusterResult,
  DialPlanManagementGroup,
  SiteResult,
  SiteSummary,
} from '../../../../../shared/models/generated/ziroModels';
import { uniq } from 'lodash';
import {
  GroupSettingsLocationFieldData,
  GroupSettingsLocationFieldEntity,
  GroupSettingsLocationOptionType,
  ZiroGroupSettingsLocationConfig,
} from './dial-pan-management-group-settings-location/dial-plan-management-group-settings-location.component';
import { ClusterOption, SiteOption } from '../dial-plan-management-edit.component';

export interface CiscoGroupSettingsFormEntity {
  name: string;
  siteIds: number[];
  clusterId: number;
}

export interface CiscoGroupSettingsFormData {
  name: string;
  location: GroupSettingsLocationFieldEntity;
}

@Component({
  selector: 'ziro-cisco-dial-plan-management-group-settings-form',
  templateUrl: './dial-plan-management-group-settings-form.component.html',
  providers: [],
  standalone: false,
})
export class CiscoDialPlanManagementGroupSettingsFormComponent
  extends ZiroFormAbstractDirective<CiscoGroupSettingsFormEntity, CiscoGroupSettingsFormData>
  implements OnInit
{
  protected _ziroFormStateService: ZiroFormStateService;

  readonly clusterOptions = input<ClusterOption[]>([]);
  readonly siteOptions = input<SiteOption[]>([]);
  readonly groupId = input<string>();
  readonly dialPlanGroups = input<DialPlanManagementGroup[]>([]);
  readonly siteSummary = input<SiteSummary>();

  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    this.formConfig = {
      fields: {
        name: {
          label: 'tkey;dialplanmanagement.admin.group.name',
          dataAutomation: 'group-settings-group-name',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                const filteredGroups = this.groupId()
                  ? this.dialPlanGroups().filter((group: DialPlanManagementGroup) => group.id !== this.groupId())
                  : this.dialPlanGroups();
                const hasMatch = filteredGroups.find(
                  (group: DialPlanManagementGroup) => group.name.toLowerCase() === val.trim().toLowerCase(),
                );
                return hasMatch ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;dialplanmanagement.admin.group.name.alreadyInUse',
            },
          ],
        },
        location: {
          dataAutomation: 'group-settings-location',
          required: false,
          componentConfig: new ZiroGroupSettingsLocationConfig([
            {
              value: GroupSettingsLocationOptionType.SITES,
              label: 'tkey;dialplanmanagement.admin.group.sites',
              options: [...this.siteOptions()],
              isMultiSelect: true,
            },
            {
              value: GroupSettingsLocationOptionType.CLUSTER,
              label: 'tkey;dialplanmanagement.admin.group.clusters.label',
              options: [...this.clusterOptions()],
              isMultiSelect: false,
            },
          ]),
          validation: [
            {
              validator: (val: GroupSettingsLocationFieldData) => {
                if (val.option === GroupSettingsLocationOptionType.SITES) {
                  const clusterIds = val.siteIds.map((o: ZiroSelectOption) => {
                    return this.siteSummary().clusters.find((cluster: ClusterResult) => {
                      return cluster.sites.find((site: SiteResult) => site.id === o.value);
                    }).id;
                  });
                  return uniq(clusterIds).length > 1
                    ? ZiroFormsValidationState.INVALID
                    : ZiroFormsValidationState.VALID;
                }

                return ZiroFormsValidationState.VALID;
              },
              message: 'tkey;dialplanmanagement.admin.group.site_cluster.validation.message',
            },
            {
              validator: (val: GroupSettingsLocationFieldData) => {
                return this.isFormSubmitted && !val.siteIds.length && !val.clusterId
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: 'tkey;validators.global.required.error',
            },
          ],
        },
      },
    };
  }

  toFormData = (entity: CiscoGroupSettingsFormEntity): CiscoGroupSettingsFormData => {
    return {
      name: entity.name,
      location: {
        clusterId: entity.clusterId,
        siteIds: entity.siteIds,
      },
    };
  };

  toEntity = (formData: CiscoGroupSettingsFormData): CiscoGroupSettingsFormEntity => {
    return {
      name: formData.name,
      siteIds: formData.location.siteIds,
      clusterId: formData.location.clusterId,
    };
  };

  submit(): Observable<null> {
    return of(null);
  }
}
